<template>
  <!-- Dialogue centré, comme le micro-prompt technique : le parent sort d'un
       paywall plein écran, on ne lui oppose pas une deuxième feuille pleine
       hauteur. Une action, une sortie aussi visible qu'elle (HARMONIA-93). -->
  <AppSheet
    mode="centered"
    :is-open="isOpen"
    data-testid="start-with-one"
    @update:is-open="onOpenChange"
    @did-dismiss="onDidDismiss"
  >
    <div class="px-6 pt-6 pb-5 text-center">
      <span class="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-[14px] bg-joy-soft text-brand-deep">
        <LucideIcon name="list-checks" :size="20" />
      </span>

      <h2 class="text-[18px] font-semibold leading-snug text-ink text-balance">
        {{ t('paywall.multikids.startWithOne.title', { name: childName }) }}
      </h2>
      <p class="mt-1.5 text-[14px] leading-relaxed text-ink-3 text-balance">
        {{ t('paywall.multikids.startWithOne.body', { name: childName }) }}
      </p>

      <div class="mt-5 flex flex-col gap-2">
        <Button
          expand="block"
          color="primary"
          data-testid="start-with-one-cta"
          @click="accept"
        >
          {{ t('paywall.multikids.startWithOne.cta') }}
        </Button>
        <Button
          expand="block"
          color="light"
          color-variant="clear"
          data-testid="start-with-one-later"
          @click="later"
        >
          {{ t('paywall.multikids.startWithOne.later') }}
        </Button>
      </div>
    </div>
  </AppSheet>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import AppSheet from '@/components/common/AppSheet.vue'
import LucideIcon from '@/components/common/LucideIcon.vue'
import { Button } from '@/components/ui/button'
import { useStartWithOne } from '@/composables/useStartWithOne'
import { useSelectedChild } from '@/composables/useSelectedChild'
import { requestTaskSpotlight } from '@/composables/useTaskSpotlight'

const { t } = useI18n({ useScope: 'global' })
const router = useRouter()
const { isOpen, childId, childName, accept, later, onDismissed } = useStartWithOne()
const { selectChild } = useSelectedChild()

/*
 * Balayage et fond touché passent ici sans passer par `later()` : l'issue est
 * déjà « repoussée » par défaut, et appeler `later()` APRÈS un tap sur le CTA
 * (Ionic émet `update:isOpen` avant `did-dismiss`) effacerait l'acceptation.
 */
function onOpenChange(open: boolean) {
  isOpen.value = open
}

/*
 * On ne quitte l'écran qu'une fois la carte partie : naviguer pendant son
 * animation de sortie laisse Ionic avec un overlay en vol au-dessus d'une page
 * qui change. La sélection d'abord (l'URL n'est qu'un miroir), puis l'onglet,
 * puis la demande de mise en avant que la page de l'enfant servira.
 */
function onDidDismiss() {
  if (!onDismissed()) return
  const id = childId.value
  if (!id) return
  selectChild(id)
  requestTaskSpotlight(id)
  void router.push({ path: '/tabs/tasks', query: { childId: id } })
}
</script>
